import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { FEATURES_DATA } from "@/data/features";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.neuronest.send&pcampaignid=web_share";

export default function FeatureDetailPage() {
  const { slug } = useParams();
  const feature = FEATURES_DATA[slug];
  const [activeIdx, setActiveIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveIdx(0);
    setShowVideo(false);
  }, [slug]);

  if (!feature) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-[#2F3A56] mb-4">
            Feature not found
          </h1>
          <Link to="/" className="text-brand-teal hover:underline">
            Back to NeuroNest
          </Link>
        </div>
      </div>
    );
  }

  const hasScreenshots = feature.screenshots.length > 0;
  const Icon = feature.icon;

  return (
    <div
      data-testid="feature-detail-page"
      className="min-h-screen bg-[#F8F9FB]"
    >
      <SEO
        title={feature.title}
        description={feature.description}
        path={`/features/${slug}`}
      />
      <Navbar />

      <main className="pt-24 md:pt-28 pb-16">
        {/* Back link */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 mb-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="feature-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#5A6376] hover:text-brand-teal transition-colors"
          >
            <ArrowLeft size={16} />
            Back to all features
          </button>
        </div>

        {/* Hero */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div
            className={`grid grid-cols-1 ${hasScreenshots ? "lg:grid-cols-2" : ""} gap-12 lg:gap-20 items-start`}
          >
            {/* Text content */}
            <div className={`${!hasScreenshots ? "max-w-2xl" : ""}`}>
              <div
                className={`w-14 h-14 rounded-2xl ${feature.bgColor} flex items-center justify-center mb-6`}
              >
                <Icon
                  size={26}
                  className={feature.iconColor}
                  strokeWidth={1.5}
                />
              </div>

              <h1
                data-testid="feature-title"
                className="font-heading text-3xl md:text-5xl font-bold text-[#2F3A56] leading-tight mb-3"
              >
                {feature.title}
              </h1>
              <p className="text-lg md:text-xl text-brand-teal font-medium mb-6">
                {feature.tagline}
              </p>
              <p className="text-base md:text-lg text-[#5A6376] leading-relaxed mb-4">
                {feature.description}
              </p>
              <p className="text-sm md:text-base text-[#5A6376] leading-relaxed mb-8">
                {feature.extendedDescription}
              </p>

              {/* Key Points */}
              <div className="space-y-3 mb-10">
                {feature.keyPoints.map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-brand-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check
                        size={12}
                        className="text-brand-teal"
                        strokeWidth={2.5}
                      />
                    </div>
                    <span className="text-sm text-[#2F3A56]">{point}</span>
                  </div>
                ))}
              </div>

              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="feature-cta-button"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/google-play-badge.png`}
                  alt="Get it on Google Play"
                  className="h-12"
                />
              </a>
            </div>

            {/* Phone Mockup Gallery */}
            {(hasScreenshots || feature.video) && (
              <div className="flex flex-col items-center lg:sticky lg:top-28">
                {/* Video/Screenshots toggle */}
                {feature.video && hasScreenshots && (
                  <div className="flex items-center gap-2 mb-5 bg-[#F0F1F3] rounded-full p-1">
                    <button
                      data-testid="toggle-video"
                      onClick={() => setShowVideo(true)}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                        showVideo
                          ? "bg-brand-teal text-white shadow-sm"
                          : "text-[#5A6376] hover:text-brand-teal"
                      }`}
                    >
                      Video
                    </button>
                    <button
                      data-testid="toggle-screenshots"
                      onClick={() => setShowVideo(false)}
                      className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all duration-300 ${
                        !showVideo
                          ? "bg-brand-teal text-white shadow-sm"
                          : "text-[#5A6376] hover:text-brand-teal"
                      }`}
                    >
                      Screenshots
                    </button>
                  </div>
                )}

                {/* Phone frame */}
                <div className="relative w-[280px] md:w-[300px]">
                  <div className="relative bg-[#2F3A56] rounded-[40px] p-[10px] shadow-[0_20px_60px_rgba(47,58,86,0.25)]">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[100px] h-[22px] bg-[#2F3A56] rounded-b-2xl z-10" />
                    {/* Screen */}
                    <div className="relative rounded-[30px] overflow-hidden bg-[#F0F1F3] min-h-[400px]">
                      {(showVideo || (!hasScreenshots && feature.video)) ? (
                        <iframe
                          data-testid="feature-video"
                          src={`${feature.video}?autoplay=0&rel=0&modestbranding=1`}
                          title={`${feature.title} video`}
                          className="w-full aspect-[9/16] block"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope"
                          allowFullScreen
                          frameBorder="0"
                        />
                      ) : (
                        <img
                          data-testid="feature-screenshot"
                          src={feature.screenshots[activeIdx].url}
                          alt={feature.screenshots[activeIdx].caption}
                          className="w-full h-auto block"
                          loading="eager"
                        />
                      )}
                    </div>
                    {/* Home indicator */}
                    <div className="absolute bottom-[6px] left-1/2 -translate-x-1/2 w-[100px] h-[4px] bg-white/30 rounded-full" />
                  </div>

                  {/* Navigation arrows (screenshots only) */}
                  {!showVideo && hasScreenshots && feature.screenshots.length > 1 && (
                    <>
                      <button
                        data-testid="screenshot-prev"
                        className="absolute left-[-48px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-[#5A6376] hover:text-brand-teal transition-colors disabled:opacity-30"
                        onClick={() => setActiveIdx((p) => Math.max(0, p - 1))}
                        disabled={activeIdx === 0}
                        aria-label="Previous screenshot"
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        data-testid="screenshot-next"
                        className="absolute right-[-48px] top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center text-[#5A6376] hover:text-brand-teal transition-colors disabled:opacity-30"
                        onClick={() =>
                          setActiveIdx((p) =>
                            Math.min(feature.screenshots.length - 1, p + 1)
                          )
                        }
                        disabled={activeIdx === feature.screenshots.length - 1}
                        aria-label="Next screenshot"
                      >
                        <ChevronRight size={18} />
                      </button>
                    </>
                  )}
                </div>

                {/* Caption */}
                {!showVideo && hasScreenshots && (
                  <p className="text-sm text-[#5A6376] text-center mt-6 max-w-[300px]">
                    {feature.screenshots[activeIdx].caption}
                  </p>
                )}
                {(showVideo || (!hasScreenshots && feature.video)) && (
                  <p className="text-sm text-[#5A6376] text-center mt-6 max-w-[300px]">
                    Watch the {feature.title} overview
                  </p>
                )}

                {/* Dots (screenshots only) */}
                {!showVideo && hasScreenshots && feature.screenshots.length > 1 && (
                  <div className="flex items-center gap-2 mt-4">
                    {feature.screenshots.map((_, i) => (
                      <button
                        key={i}
                        data-testid={`screenshot-dot-${i}`}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          i === activeIdx
                            ? "bg-brand-teal scale-110"
                            : "bg-[#E7E9EC] hover:bg-[#D0D3D8]"
                        }`}
                        onClick={() => setActiveIdx(i)}
                        aria-label={`View screenshot ${i + 1}`}
                      />
                    ))}
                  </div>
                )}

                {/* Thumbnails (screenshots only) */}
                {!showVideo && hasScreenshots && feature.screenshots.length > 1 && (
                  <div className="flex items-center gap-3 mt-6">
                    {feature.screenshots.map((s, i) => (
                      <button
                        key={i}
                        data-testid={`screenshot-thumb-${i}`}
                        className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                          i === activeIdx
                            ? "border-brand-teal shadow-md"
                            : "border-transparent opacity-50 hover:opacity-80"
                        }`}
                        onClick={() => setActiveIdx(i)}
                      >
                        <img
                          src={s.url}
                          alt={`Thumbnail ${i + 1}`}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
